/**
 * src/lib/voice/micRegistry.js — Global microphone / MediaRecorder lifecycle registry.
 *
 * 2026-04-20 — CEO reported a persistent bug across multiple voice entry
 * points (top-banner breadcrumbs, voice overlay, entity mics, dictation):
 * the mic stays active after the user submits the recording. Desktop shows
 * the red tab-dot; mobile has no visual cue and silently accumulates audio
 * chunks (the 14,086-chunk orphan observed in app_error_logs at 05:53).
 *
 * Root cause: each mic hook (useEntityMic, useWhisperRecording,
 * useInterpreter, useChunkedRecorder, useVoiceClassify, useDictation) has
 * its own getUserMedia + MediaRecorder pair and its own cleanup. Some miss
 * edge cases (rotateRecorder chain, onstop not firing, component unmount
 * before recorder.stop). No single kill-switch covers them all.
 *
 * This module is the single source of truth. Every hook:
 *   1. calls registerMic(stream, recorder) when it acquires a stream
 *   2. calls releaseMic(stream) when it cleanly stops
 *
 * And the registry auto-releases on:
 *   - window 'beforeunload'
 *   - document visibilitychange === 'hidden' (user backgrounds tab / app)
 *   - page 'pagehide' (iOS Safari special case)
 *
 * stopAllMics() is also exported for emergency use from any caller.
 *
 * The registry is a singleton stored on window so it survives React
 * Strict Mode double-mounts and hot reloads.
 */

const REGISTRY_KEY = '__acn_mic_registry__';

function getRegistry() {
  if (typeof window === 'undefined') return null;
  if (!window[REGISTRY_KEY]) {
    window[REGISTRY_KEY] = {
      entries: new Map(), // stream -> { recorder, label, registeredAt }
      listenersAttached: false,
    };
  }
  const reg = window[REGISTRY_KEY];
  if (!reg.listenersAttached && typeof document !== 'undefined') {
    reg.listenersAttached = true;
    const onHide = () => { if (document.visibilityState === 'hidden') stopAllMics('visibility_hidden'); };
    window.addEventListener('beforeunload', () => stopAllMics('beforeunload'));
    window.addEventListener('pagehide', () => stopAllMics('pagehide'));
    document.addEventListener('visibilitychange', onHide);
  }
  return reg;
}

/**
 * Register an active mic stream + (optionally) a MediaRecorder using it.
 * Callers SHOULD also call releaseMic(stream) when they cleanly stop, but
 * the auto-release listeners catch the rest.
 *
 * @param {MediaStream} stream
 * @param {MediaRecorder|null} recorder
 * @param {string} [label] — human-readable identifier for debugging
 */
export function registerMic(stream, recorder = null, label = 'unknown') {
  const reg = getRegistry();
  if (!reg || !stream) return;
  reg.entries.set(stream, {
    recorder,
    label,
    registeredAt: Date.now(),
  });
}

/**
 * Release a single mic registration + stop its tracks + stop its recorder.
 * Idempotent; safe to call multiple times.
 *
 * @param {MediaStream} stream
 */
export function releaseMic(stream) {
  const reg = getRegistry();
  if (!reg || !stream) return;
  const entry = reg.entries.get(stream);
  if (entry?.recorder) {
    try {
      if (entry.recorder.state !== 'inactive') entry.recorder.stop();
    } catch { /* noop */ }
  }
  try {
    stream.getTracks().forEach(t => { try { t.stop(); } catch { /* noop */ } });
  } catch { /* noop */ }
  reg.entries.delete(stream);
}

/**
 * Nuclear option — stop every registered mic. Called automatically on
 * beforeunload / pagehide / visibilitychange=hidden. Also callable from
 * user code (e.g., a "force release mic" debug action).
 *
 * @param {string} [reason] — surfaced in console for diagnostics
 * @returns {number} count of mics stopped
 */
export function stopAllMics(reason = 'manual') {
  const reg = getRegistry();
  if (!reg) return 0;
  const count = reg.entries.size;
  if (count > 0) {
    // eslint-disable-next-line no-console -- surfacing this is the point
    console.warn(`[micRegistry] stopAllMics(${reason}) releasing ${count} stream(s)`);
  }
  const streams = Array.from(reg.entries.keys());
  for (const stream of streams) releaseMic(stream);
  return count;
}

/**
 * How many mics are currently active. Useful for the agent to self-check
 * "is there something recording right now?" before reporting status.
 */
export function activeMicCount() {
  const reg = getRegistry();
  if (!reg) return 0;
  return reg.entries.size;
}

/**
 * Enumerate active mics for diagnostics (label + age).
 */
export function snapshotMics() {
  const reg = getRegistry();
  if (!reg) return [];
  const now = Date.now();
  return Array.from(reg.entries.values()).map(e => ({
    label: e.label,
    ageMs: now - e.registeredAt,
  }));
}
